<!--
  A styled collection of checkboxes.
  Will update an array of items.
  
  "options" prop is required, and should be an object with keys representing the human-readable option names to be displayed in the dropdown,
  and values representing the corresponding model values which we're actually binding to.

 -->
<template>
  <div class="fieldWrapper">

    <div style="display:flex">
      <label v-if="label" class="label">{{ label }}</label>
      <slot name="top"/>
    </div>

    <p v-if="description" class="mb-2">{{ description }}</p>

    <div class="control checkboxGroup" :class="{stacked: stacked}">

      <label class="checkbox checkboxLabel"
        v-for="(optionValue, optionLabel) in allowedOptions"
        :key="optionValue">

        <input
          ref="inputElem"
          type="checkbox"
          class="mr-3"
          :checked="modelValue ? modelValue.includes(optionValue) : false"
          @change="onChanged(optionValue)"
        />
        {{ optionLabel }}

      </label>

      <slot />

    </div>

  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

const props = defineProps<{
  options: object,
  includedOptions?: any[],
  excludedOptions?: any[],
  modelValue?: any[]|null,
  label?: string,
  description?: string,
  stacked?: boolean,
  validator?: (fieldId: any, val: any) => boolean,
  validationId?: string,
  isDisabled?: boolean
}>();

// Event for two-way binding
const emit = defineEmits(['update:modelValue']);
const onChanged = (val: any) => {
  // Toggle val
  const items = props.modelValue || [];

  if (items.includes(val)) {
    emit('update:modelValue', items.filter(i => i != val));
  }
  else {
    emit('update:modelValue', [val, ...items]);
  }
};

const allowedOptions = computed(() => {
  if (props.includedOptions) {
    return Object.fromEntries(
      Object.entries(props.options).filter(([k,v]) => props.includedOptions!.includes(v))
    );
  } else if (props.excludedOptions) {
    return Object.fromEntries(
      Object.entries(props.options).filter(([k,v]) => !props.excludedOptions!.includes(v))
    );
  } else {
    return props.options;
  }  
});

</script>

<style lang="scss" scoped>
@import "../assets/variables.scss";

.checkboxGroup {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.75rem;
}

@media screen and (max-width: $tabletWidth) {
.checkboxGroup {
    align-items: start;
    flex-direction: column;
    gap: 0rem;
  }
}

.stacked {
  align-items: start;
  flex-direction: column;
  gap: 0rem;
}

.checkboxLabel {
  padding: 0.5rem;
}

input[type=checkbox] {
  scale: 150%;
  position: relative;
  left: 0.25rem;
  top: -0.1rem;
}
</style>
 